import React, { useContext, useEffect, useState } from 'react'
import { Helmet } from 'react-helmet-async'
import { useNavigate, useParams } from 'react-router-dom'

import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Card from '@mui/material/Card'
import Chip from '@mui/material/Chip'
import CircularProgress from '@mui/material/CircularProgress'
import Container from '@mui/material/Container'
import Fab from '@mui/material/Fab'
import FormControlLabel from '@mui/material/FormControlLabel'
import Grid from '@mui/material/Grid'
import Snackbar from '@mui/material/Snackbar'
import Stack from '@mui/material/Stack'
import Switch from '@mui/material/Switch'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'

import DeleteIcon from '@mui/icons-material/Delete'
import SaveIcon from '@mui/icons-material/Save'

import { ThemeContext, UserContext } from 'components/common/App'

import rpc, { buildRequest, signoutAndRedirectToIndex } from 'lib/rpc'

/**
 * Create or edit a themed pack (#278) — scalar fields + a member multi-select
 * grid. `packId` in the route → edit mode (loads the pack + preselects members
 * and exposes Delete); no param → create mode. Membership is exclusive, so a
 * mascot picked here leaves any other pack on save.
 */
export default function PackEditPage() {
  const theme = useContext(ThemeContext)
  // Creating/editing/deleting packs is superuser-only (#290).
  const currentUser = useContext(UserContext)
  const isSuperuser = currentUser && currentUser.role === "superuser"
  const navigate = useNavigate()
  const { packId } = useParams()
  const isEdit = Boolean(packId)

  const [mascots, setMascots] = useState(null)
  const [slug, setSlug] = useState("")
  const [name, setName] = useState("")
  const [description, setDescription] = useState("")
  const [displayOrder, setDisplayOrder] = useState(0)
  const [isActive, setIsActive] = useState(true)
  const [members, setMembers] = useState([]) // slugs
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)
  const [saved, setSaved] = useState(false)

  useEffect(() => {
    theme.changeTheme("light-bg")
    window.scrollTo(0, 0)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    const requests = [
      rpc.post("", buildRequest("admin.mascots.list", { page: 1, per_page: 200, search: "" })),
    ]
    if (isEdit) {
      requests.push(
        rpc.post("", buildRequest("admin.mascots.packs.get", { pack_id: Number(packId) }))
      )
    }
    Promise.all(requests).then((responses) => {
      const list = responses[0].data.result
      if (list.message) {
        if (list.code === 5) signoutAndRedirectToIndex()
        setError(list.message)
        return
      }
      setMascots(list.data.filter((m) => m.is_active && m.expressions && m.expressions.idle))
      if (isEdit) {
        const pack = responses[1].data.result
        if (pack.message) {
          setError(pack.message)
          return
        }
        setSlug(pack.slug)
        setName(pack.name)
        setDescription(pack.description || "")
        setDisplayOrder(pack.display_order)
        setIsActive(pack.is_active)
        setMembers(pack.members || [])
      }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const toggleMember = (memberSlug) => {
    setMembers((prev) =>
      prev.includes(memberSlug) ? prev.filter((s) => s !== memberSlug) : [...prev, memberSlug]
    )
  }

  const save = () => {
    setSaving(true)
    setError(null)
    const payload = {
      slug,
      name,
      description,
      display_order: Number(displayOrder) || 0,
      is_active: isActive,
      members,
    }
    const request = isEdit
      ? buildRequest("admin.mascots.packs.update", { pack_id: Number(packId), ...payload })
      : buildRequest("admin.mascots.packs.create", payload)
    rpc.post("", request).then((response) => {
      const result = response.data.result
      if (result.message) {
        if (result.code === 5) signoutAndRedirectToIndex()
        setError(result.message)
      } else if (!isEdit) {
        navigate(`/mascots/packs/details/${result.id}`)
      } else {
        setMembers(result.members || [])
        setSaved(true)
      }
    }).finally(() => setSaving(false))
  }

  const remove = () => {
    if (!window.confirm("Delete this pack? Its mascots stay, just ungrouped.")) {
      return
    }
    rpc.post(
      "",
      buildRequest("admin.mascots.packs.delete", { pack_id: Number(packId) })
    ).then((response) => {
      const result = response.data.result
      if (result.message) {
        if (result.code === 5) signoutAndRedirectToIndex()
        setError(result.message)
      } else {
        navigate("/mascots/packs")
      }
    })
  }

  if (mascots === null) {
    return (
      <Container fixed sx={{ textAlign: 'center', py: 8 }}>
        <CircularProgress />
      </Container>
    )
  }

  const canSave = slug.trim() && name.trim() && !saving

  return (
    <>
      <Helmet>
        <title>{isEdit ? "Edit Pack" : "New Pack"}</title>
      </Helmet>

      {isSuperuser && (
        <Fab
          color="primary"
          aria-label="save"
          sx={{ position: 'fixed', bottom: 16, right: 16 }}
          disabled={!canSave}
          onClick={save}
        >
          <SaveIcon />
        </Fab>
      )}

      <Container fixed sx={{ pb: 10 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h4" component="h1" gutterBottom>
            {isEdit ? "Edit Pack" : "New Pack"}
          </Typography>
          {isEdit && isSuperuser && (
            <Button color="error" startIcon={<DeleteIcon />} onClick={remove}>
              Delete
            </Button>
          )}
        </Stack>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Stack spacing={2} sx={{ mb: 3 }}>
          <TextField
            label="Slug"
            value={slug}
            onChange={(e) => setSlug(e.target.value)}
            helperText="Lowercase id, e.g. ocean, founders"
            required
          />
          <TextField label="Name" value={name} onChange={(e) => setName(e.target.value)} required />
          <TextField
            label="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            multiline
            minRows={2}
          />
          <TextField
            label="Display order"
            type="number"
            value={displayOrder}
            onChange={(e) => setDisplayOrder(e.target.value)}
            sx={{ maxWidth: 200 }}
          />
          <FormControlLabel
            control={<Switch checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />}
            label="Active (shown as a group in the catalog)"
          />
        </Stack>

        <Stack direction="row" spacing={1} sx={{ mb: 1 }} alignItems="center">
          <Typography variant="h6">Members</Typography>
          <Chip size="small" label={`${members.length} selected`} />
        </Stack>
        <Typography color="text.secondary" sx={{ mb: 2 }}>
          A mascot can be in one pack at a time — picking it here moves it out of any other pack on save.
        </Typography>

        <Grid container spacing={2}>
          {mascots.map((mascot) => {
            const selected = members.includes(mascot.slug)
            return (
              <Grid item xs={6} sm={4} md={3} key={mascot.slug}>
                <Card
                  variant="outlined"
                  sx={{
                    p: 1.5,
                    textAlign: 'center',
                    borderWidth: selected ? 2 : 1,
                    borderColor: selected ? 'primary.main' : 'divider',
                  }}
                >
                  <Box
                    sx={{ height: 80, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                  >
                    <img
                      src={mascot.expressions.idle}
                      alt={mascot.name}
                      style={{ maxHeight: 72, maxWidth: '100%' }}
                    />
                  </Box>
                  <Typography variant="subtitle2" noWrap>
                    {mascot.name}
                  </Typography>
                  <Button
                    size="small"
                    fullWidth
                    sx={{ mt: 1 }}
                    variant={selected ? 'contained' : 'outlined'}
                    onClick={() => toggleMember(mascot.slug)}
                  >
                    {selected ? 'In pack' : 'Add'}
                  </Button>
                </Card>
              </Grid>
            )
          })}
        </Grid>
      </Container>

      <Snackbar
        open={saved}
        autoHideDuration={3000}
        onClose={() => setSaved(false)}
        message="Pack saved"
      />
    </>
  )
}
